import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLiveQuery } from "@tanstack/react-db";
import { storesCollection, eq, type Store } from "@cliqsuite/db";
import { useAuth } from "./auth";

interface StoreScopeState {
  stores: Store[];
  storeId: string | null;
  store: Store | null;
  setStoreId: (id: string) => void;
  isLoading: boolean;
}

const StoreScopeContext = createContext<StoreScopeState | null>(null);

/** Every business_id-scoped page reads the active store from here instead of re-picking one itself. */
export function StoreScopeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const businessId = user?.business_id;
  const { data: allStores, isLoading } = useLiveQuery(
    (q) =>
      businessId
        ? q.from({ stores: storesCollection }).where(({ stores }) => eq(stores.business_id, businessId))
        : q.from({ stores: storesCollection }).where(({ stores }) => eq(stores.id, "__none__")),
    [businessId],
  );
  const [storeId, setStoreId] = useState<string | null>(null);

  // Empty store_ids (the owner, or a manager created before this field
  // existed) means every store; a non-empty array restricts the switcher
  // to just those — client-side only, matching the rest of Access
  // Control's permission model (see core.business_users.store_ids).
  const allowedStoreIds = user?.store_ids;
  const stores = useMemo(() => {
    if (!allStores) return allStores;
    if (!allowedStoreIds || allowedStoreIds.length === 0) return allStores;
    const allowed = new Set(allowedStoreIds);
    return allStores.filter((s) => allowed.has(s.id));
  }, [allStores, allowedStoreIds]);

  useEffect(() => {
    if (!storeId && stores && stores.length > 0) setStoreId(stores[0]!.id);
  }, [stores, storeId]);

  const value = useMemo<StoreScopeState>(() => {
    const store = stores?.find((s) => s.id === storeId) ?? stores?.[0] ?? null;
    return { stores: stores ?? [], storeId: store?.id ?? null, store, setStoreId, isLoading };
  }, [stores, storeId, isLoading]);

  return <StoreScopeContext.Provider value={value}>{children}</StoreScopeContext.Provider>;
}

export function useStoreScope(): StoreScopeState {
  const ctx = useContext(StoreScopeContext);
  if (!ctx) throw new Error("useStoreScope must be used within StoreScopeProvider");
  return ctx;
}
